import React, { useState } from 'react';
import { LLink as Link } from '../components/LocalizedLink';
import { Mail, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (err.message || 'Something went wrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f7fafd] py-16">
      <div className="container-x max-w-md mx-auto">
        <div className="flex flex-col items-center mb-6">
          <span className="w-12 h-12 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
            <Mail size={20} />
          </span>
          <h1 className="mt-4 text-2xl font-bold text-[#0b1f3a]">Reset your password</h1>
          <p className="text-slate-600 text-sm mt-1 text-center">
            Enter your email and we&apos;ll send you a link to choose a new password.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <span className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center mb-3">
                <CheckCircle2 size={22} />
              </span>
              <h3 className="text-lg font-semibold text-[#0b1f3a]">Check your inbox</h3>
              <p className="text-slate-600 text-sm mt-2">
                If an account exists for <b>{email}</b>, we&apos;ve sent a reset link.
                The link is valid for 1 hour.
              </p>
              <p className="text-slate-500 text-xs mt-3">
                Don&apos;t see it? Check your spam folder, or try again in a few minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              {error && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle size={16} className="mt-0.5" /> {error}
                </div>
              )}
              <div>
                <Label className="field-label">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center disabled:opacity-60"
                data-testid="forgot-submit"
              >
                {busy ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link
            to="/login"
            className="text-[#0d3a8a] font-semibold hover:underline inline-flex items-center gap-1"
            data-testid="forgot-back-login"
          >
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
